import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { ladderBody } from '@/settings/follow-ups/channels'
import type { Step } from '@/settings/follow-ups/channels'
import { FollowUpsForm } from '@/settings/follow-ups/form'
import { ErrorState, Loading } from '@/ui/states'
import { SettingsStepShell } from './SettingsStepShell'
import { useSettleStep } from './settingsStep'

/** Step 9 — the follow-up ladder and daily cap, through `/settings/follow-ups`'s form. */
export function FollowUpsStep(): JSX.Element {
  const compose = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/brief', { signal })),
    [],
  )
  const saveLadder = useMutation((steps: Step[]) =>
    call(() => api.PUT('/api/v1/outreach/follow-ups/ladder', { body: ladderBody(steps) })),
  )
  const saveCap = useMutation((value: number) =>
    call(() => api.PUT('/api/v1/outreach/follow-ups/cap', { body: { follow_ups_per_day: value } })),
  )
  const { settle, failure } = useSettleStep('follow_ups')

  return (
    <SettingsStepShell step="follow_ups" failure={failure}>
      {compose.state.status === 'loading' ? <Loading label="Loading" lines={2} /> : null}
      {compose.state.status === 'error' ? (
        <ErrorState failure={compose.state.failure} onRetry={compose.refetch} />
      ) : null}
      {compose.state.status === 'ready' ? (
        <FollowUpsForm
          value={{ steps: compose.state.data.steps, cap: String(compose.state.data.daily_cap) }}
          submitLabel="Continue"
          onSubmit={async (next) => {
            const ladder = await saveLadder.run(next.steps)
            if (!ladder.ok) return ladder
            const capped = await saveCap.run(next.cap)
            if (!capped.ok) return capped
            await settle()
            return capped
          }}
        />
      ) : null}
    </SettingsStepShell>
  )
}
